import Accordion from './Accordion.jsx';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BoundaryMap from './BoundaryMap.jsx';
import CustomerAccountAutocomplete from '../common/customer-account-autocomplete/CustomerAccountAutocomplete.jsx';
import DateRangePicker from './DateRangePicker.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import Header from '../common/layout/Header.jsx';
import Notifier from '../common/Notifier.jsx';
import OrgAutocomplete from '../common/org-autocomplete/OrgAutocomplete.jsx';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import UserAutocomplete from './UserAutocomplete.jsx';
import isEqual from 'lodash/isEqual.js';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers-pro';
import {DateTime} from 'luxon';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {getWorkspacesForCA} from '@account/api/products.js';
import {
  useCreatePolicy,
  useGetPolicy,
  useUpdatePolicy,
} from '@account/api/archiveControl.js';
import {useGetCollection, useGetCollections} from '@account/api/features.js';
import {useGetItemTypes} from '@account/api/itemTypes.js';
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min.js';
import {useQuery} from '@tanstack/react-query';
import {useUIMode} from '@hooks/useUIMode';

const haveToisChanged = (ranges1, ranges2) => {
  if (ranges1.length !== ranges2.length) {
    return true;
  }
  const hasChanged = ranges1.some(
    (range, index) =>
      range.start !== ranges2[index]?.start || range.end !== ranges2[index]?.end
  );
  return hasChanged;
};

const dateRangesToISO = ranges =>
  ranges.map(range => ({
    start: range.start?.toISO(),
    end: range.end?.toISO(),
  }));

// Compare a selection ([{id}, ...]) against a baseline id list by set, not by
// length - so a swap (remove one, add one) is still detected as a change.
// Length-only comparison left Save disabled on a net-zero edit (PRODISSUE-1792).
const idSetChanged = (selected, baselineIds = []) =>
  !isEqual(
    [...selected.map(s => s.id)].sort(),
    [...(baselineIds ?? [])].sort()
  );

// WS/CA exemptions degrade gracefully: send the arrays only when the admin has
// selections, or when editing a policy that already carries them (so a cleared
// selection persists as []). Otherwise omit them so create/edit keeps working
// against a backend that predates these fields.
export const buildExemptionPayload = ({
  selectedCAs,
  selectedWorkspaces,
  policy,
}) => {
  const backendHasExemptions =
    !!policy &&
    ('excluded_customer_account_ids' in policy ||
      'excluded_workspace_ids' in policy);
  const out = {};
  if (selectedCAs.length || backendHasExemptions) {
    out.excluded_customer_account_ids = selectedCAs.map(ca => ca.id);
  }
  if (selectedWorkspaces.length || backendHasExemptions) {
    out.excluded_workspace_ids = selectedWorkspaces.map(ws => ws.id);
  }
  return out;
};

const PolicyForm = () => {
  const {policyId} = useParams();
  const history = useHistory();
  const {isNewUI} = useUIMode();

  const [name, setName] = useState('');
  const [collectionInputValue, setCollectionInputValue] = useState('');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [delay, setDelay] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [tois, setTois] = useState([]);
  const [selectedCAs, setSelectedCAs] = useState([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [wsBrowseCA, setWsBrowseCA] = useState(null);

  const itemTypesQuery = useGetItemTypes();
  const featureCollectionsQuery = useGetCollections();
  const policyQuery = useGetPolicy(
    {policyId},
    {
      enabled: !!policyId,
    }
  );

  const policy = policyQuery?.data;

  const collectionQuery = useGetCollection(
    {collectionId: policy?.collectionId},
    {
      enabled: !!policy && !!policy?.collectionId,
    }
  );

  const workspacesQuery = useQuery({
    queryKey: ['workspaces-for-ca', wsBrowseCA?.id],
    queryFn: () => getWorkspacesForCA(wsBrowseCA.id),
    enabled: !!wsBrowseCA?.id,
  });
  const browseWorkspaces = workspacesQuery.data?.workspaces ?? [];

  const updatePolicy = useUpdatePolicy({
    onSuccess: () => {
      history.push(`${Routes.ARCHIVE_CONTROL}/${policyId}`);
    },
  });
  const createPolicy = useCreatePolicy({
    onSuccess: data => {
      history.push(`${Routes.ARCHIVE_CONTROL}/${data.id}`);
    },
  });

  const formHasChanged =
    policy &&
    (policy?.name !== name ||
      !isEqual(policy?.body.item_types, selectedItemTypes) ||
      policy?.body.collection_ref !== selectedCollection?.['pl:ref'] ||
      idSetChanged(selectedOrgs, policy?.excluded_organization_ids) ||
      idSetChanged(selectedUsers, policy?.excluded_user_ids) ||
      idSetChanged(selectedCAs, policy?.excluded_customer_account_ids) ||
      idSetChanged(selectedWorkspaces, policy?.excluded_workspace_ids) ||
      delay !== policy.body.delay ||
      startDate?.toMillis() !==
        (policy.body.start_date
          ? DateTime.fromISO(policy.body.start_date, {zone: 'utc'}).toMillis()
          : undefined) ||
      haveToisChanged(dateRangesToISO(tois), policy.body.tois ?? []));

  const formHasRequiredFields =
    !!name &&
    selectedItemTypes.length > 0 &&
    !!selectedCollection &&
    (delay > 0 || !!startDate);

  const canSubmit = policyId
    ? formHasChanged && formHasRequiredFields
    : formHasRequiredFields;

  useEffect(() => {
    if (policy) {
      setName(policy.name);
      setSelectedItemTypes(policy.body.item_types);
      setSelectedOrgs(
        policy.excluded_organization_ids.map(orgId => ({id: orgId}))
      );
      setSelectedUsers(policy.excluded_user_ids?.map(userId => ({id: userId})));
      setSelectedCAs(
        (policy.excluded_customer_account_ids ?? []).map(id => ({id}))
      );
      setSelectedWorkspaces(
        (policy.excluded_workspace_ids ?? []).map(id => ({id}))
      );
      setDelay(policy.body.delay);
      if (policy.body.start_date) {
        setStartDate(DateTime.fromISO(policy.body.start_date, {zone: 'utc'}));
      }

      if (policy.body.tois?.length > 0) {
        const dateRanges = policy.body.tois.map(range => ({
          start: DateTime.fromISO(range.start, {zone: 'utc'}),
          end: range.end ? DateTime.fromISO(range.end, {zone: 'utc'}) : null,
        }));

        setTois(dateRanges);
      }
    }
  }, [policy]);

  useEffect(() => {
    if (collectionQuery?.data) {
      setSelectedCollection(collectionQuery?.data);
    }
  }, [collectionQuery?.data]);

  const handleSubmit = () => {
    // remove any incomplete ranges
    const toiRanges = tois.filter(range => !!range.start);

    const payloadBody = {
      collection_ref: selectedCollection['pl:ref'],
      delay: parseInt(delay, 10) ?? null,
      item_types: selectedItemTypes,
      start_date: startDate,
      toi_ranges: toiRanges,

      //backwards compat -- remove once teams migrate
      aois: [
        {
          type: 'Feature',
          geometry: {
            content: selectedCollection['pl:ref'],
            type: 'ref',
          },
        },
      ],
      delay_target: parseInt(delay, 10) ?? null,
      delay_ramp_effective_date: startDate,
    };

    const payload = {
      name,
      body: payloadBody,
      excluded_organization_ids: selectedOrgs.map(org => org.id),
      excluded_user_ids: selectedUsers.map(user => user.id),
    };

    Object.assign(
      payload,
      buildExemptionPayload({selectedCAs, selectedWorkspaces, policy})
    );

    if (policyId) {
      updatePolicy.mutate({id: policyId, ...payload});
    } else {
      createPolicy.mutate(payload);
    }
  };

  if (itemTypesQuery.isLoading || featureCollectionsQuery.isLoading) {
    return <Notifier open progress message="Loading..." />;
  }

  return (
    <Box sx={{paddingBlockEnd: 2}}>
      <Header name={`${policyId ? 'Edit' : 'Create'} Archive Control Policy`}>
        <Stack direction="row" sx={{gap: 1}}>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to={
              policyId
                ? `${Routes.ARCHIVE_CONTROL}/${policyId}`
                : Routes.ARCHIVE_CONTROL
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={
              !canSubmit || updatePolicy.isPending || createPolicy.isPending
            }
            onClick={handleSubmit}
          >
            {policyId ? 'Save' : 'Create'}
          </Button>
        </Stack>
      </Header>
      <Stack>
        <TextField
          label="Name"
          required
          value={name}
          onChange={event => {
            setName(event.target.value);
          }}
        />

        <Accordion title="Item Types">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            Select item types to which this policy applies (data matching these
            types will be blocked)
          </Typography>
          <FormGroup row sx={{marginInlineStart: 1}}>
            {itemTypesQuery.data?.map(itemType => (
              <FormControlLabel
                key={itemType.id}
                control={
                  <Checkbox
                    checked={selectedItemTypes.includes(itemType.id)}
                    onChange={event => {
                      const {name, checked} = event.target;
                      if (checked) {
                        setSelectedItemTypes(prev => [...prev, name]);
                      } else {
                        setSelectedItemTypes(prev =>
                          prev.filter(p => p !== name)
                        );
                      }
                    }}
                    name={itemType.id}
                  />
                }
                label={itemType.display_name}
              />
            ))}
            <FormControlLabel
              key="mosaics"
              control={
                <Checkbox
                  checked={selectedItemTypes.includes('basemap')}
                  onChange={event => {
                    const {name, checked} = event.target;
                    if (checked) {
                      setSelectedItemTypes(prev => [...prev, name]);
                    } else {
                      setSelectedItemTypes(prev =>
                        prev.filter(p => p !== name)
                      );
                    }
                  }}
                  name="basemap" //backwards compat key
                />
              }
              label="Mosaics"
            />
          </FormGroup>
        </Accordion>

        <Accordion title="Exclusion times">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            You must specify at least one of these fields
          </Typography>
          <Stack direction="row" sx={{gap: 1}}>
            <TextField
              type="number"
              label="Delay (days)"
              helperText="Number of days to delay availability"
              value={delay}
              onChange={event => {
                const {value} = event.target;
                setDelay(value);
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
              onKeyDown={event => {
                if (
                  event.key === '.' ||
                  event.key === ',' ||
                  event.key === '-'
                ) {
                  event.preventDefault();
                }
              }}
            />
            <DatePicker
              timezone="utc"
              label="Start date"
              format="yyyy-MM-dd"
              slotProps={{
                textField: {
                  helperText: 'Cutoff start',
                },
                field: {clearable: true},
              }}
              value={startDate}
              onChange={date => {
                setStartDate(date ? date.startOf('day') : null);
              }}
              sx={{minWidth: 300}}
            />
          </Stack>
          <Divider sx={{marginBlock: 2}} />
          <Stack sx={{gap: 2}}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                Optional: Add a date range
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setTois(prev => [
                    ...prev,
                    {
                      start: null,
                      end: null,
                    },
                  ]);
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Stack>
            {tois.map((toiRange, index) => (
              <Stack
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                direction="row"
                sx={{gap: 1, alignItems: 'center', width: '100%'}}
              >
                <DateRangePicker
                  startDate={toiRange.start}
                  onChangeStartDate={date => {
                    setTois(prev => {
                      const current = prev[index];
                      current.start = date;
                      return [...prev];
                    });
                  }}
                  endDate={toiRange.end}
                  onChangeEndDate={date => {
                    setTois(prev => {
                      const current = prev[index];
                      current.end = date;
                      return [...prev];
                    });
                  }}
                />
                <Tooltip text="Remove range">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTois(prev => prev.filter((_, idx) => index !== idx));
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Accordion>

        <Accordion title="Exclusion Area">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            The area that will be blocked from access.
          </Typography>
          <Stack sx={{gap: 2}}>
            <Autocomplete
              options={featureCollectionsQuery.data?.collections}
              loading={featureCollectionsQuery.isLoading}
              sx={{width: '50%'}}
              getOptionLabel={option => option.title ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField {...params} label="Choose a boundary" />
              )}
              value={selectedCollection}
              onChange={(event, newValue) => {
                setSelectedCollection(newValue);
              }}
              inputValue={collectionInputValue}
              onInputChange={(_, newValue) => {
                setCollectionInputValue(newValue);
              }}
            />
            <Box sx={{height: 350}}>
              <BoundaryMap collection={selectedCollection} />
            </Box>
          </Stack>
        </Accordion>

        <Accordion title="Exempt Organizations" defaultExpanded={!!policyId}>
          <Stack sx={{gap: 2, overflow: 'hidden'}}>
            <Typography variant="body2">
              Organizations below are exempt from this policy (not blocked).
            </Typography>
            <OrgAutocomplete
              id="exempt-org-autocomplete"
              showCreateOrgLink={false}
              placeholder="Search for an organization by name or ID"
              value={null}
              onChange={org => {
                // Add-only: the picker never holds the selection, so MUI's
                // empty-input Backspace can't silently drop an exempt org
                // (PRODISSUE-1792). Removal is only via the chip delete below.
                if (org && !selectedOrgs.some(s => s.id === org.id)) {
                  setSelectedOrgs(prev => [{...org, new: true}, ...prev]);
                }
              }}
            />
            <Stack
              direction="row"
              sx={{
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                maxHeight: 250,
                overflow: 'auto',
              }}
            >
              {selectedOrgs.map(org => (
                <Chip
                  shape="square"
                  key={org.id}
                  label={org.name || org.id}
                  color={org.new ? 'primary' : 'default'}
                  onDelete={() => {
                    setSelectedOrgs(prev =>
                      prev.filter(prevOrg => prevOrg.id !== org.id)
                    );
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Accordion>

        {isNewUI && (
          <>
            <Accordion
              title="Exempt Customer Accounts"
              defaultExpanded={!!policyId}
            >
              <Stack sx={{gap: 2, overflow: 'hidden'}}>
                <Typography variant="body2">
                  Entire accounts below are exempt from this policy, including
                  any workspaces created later.
                </Typography>
                <CustomerAccountAutocomplete
                  id="exempt-ca-autocomplete"
                  placeholder="Search for a customer account by name or ID"
                  value={null}
                  onChange={ca => {
                    if (ca && !selectedCAs.some(c => c.id === ca.id)) {
                      setSelectedCAs(prev => [
                        {id: ca.id, name: ca.name, new: true},
                        ...prev,
                      ]);
                    }
                  }}
                />
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    maxHeight: 250,
                    overflow: 'auto',
                  }}
                >
                  {selectedCAs.map(ca => (
                    <Chip
                      shape="square"
                      key={ca.id}
                      label={ca.name || ca.id}
                      color={ca.new ? 'primary' : 'default'}
                      onDelete={() =>
                        setSelectedCAs(prev => prev.filter(c => c.id !== ca.id))
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </Accordion>

            <Accordion title="Exempt Workspaces" defaultExpanded={!!policyId}>
              <Stack sx={{gap: 2, overflow: 'hidden'}}>
                <Typography variant="body2">
                  Specific workspaces below are exempt. Workspaces created later
                  are not exempt automatically — exempt the whole account for
                  that.
                </Typography>
                <CustomerAccountAutocomplete
                  id="ws-browse-ca-autocomplete"
                  placeholder="Pick a customer account to list its workspaces"
                  value={wsBrowseCA}
                  onChange={setWsBrowseCA}
                />
                {wsBrowseCA &&
                  (workspacesQuery.isLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading workspaces…
                    </Typography>
                  ) : browseWorkspaces.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No workspaces found for this account.
                    </Typography>
                  ) : (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={browseWorkspaces.every(w =>
                              selectedWorkspaces.some(
                                s => s.id === w.workspace_id
                              )
                            )}
                            indeterminate={
                              browseWorkspaces.some(w =>
                                selectedWorkspaces.some(
                                  s => s.id === w.workspace_id
                                )
                              ) &&
                              !browseWorkspaces.every(w =>
                                selectedWorkspaces.some(
                                  s => s.id === w.workspace_id
                                )
                              )
                            }
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedWorkspaces(prev => {
                                  const existing = new Set(prev.map(s => s.id));
                                  const additions = browseWorkspaces
                                    .filter(w => !existing.has(w.workspace_id))
                                    .map(w => ({
                                      id: w.workspace_id,
                                      name: w.name,
                                      caId: wsBrowseCA.id,
                                      new: true,
                                    }));
                                  return [...additions, ...prev];
                                });
                              } else {
                                const ids = new Set(
                                  browseWorkspaces.map(w => w.workspace_id)
                                );
                                setSelectedWorkspaces(prev =>
                                  prev.filter(s => !ids.has(s.id))
                                );
                              }
                            }}
                          />
                        }
                        label="Select all"
                      />
                      <FormGroup>
                        {browseWorkspaces.map(w => (
                          <FormControlLabel
                            key={w.workspace_id}
                            control={
                              <Checkbox
                                checked={selectedWorkspaces.some(
                                  s => s.id === w.workspace_id
                                )}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedWorkspaces(prev => [
                                      {
                                        id: w.workspace_id,
                                        name: w.name,
                                        caId: wsBrowseCA.id,
                                        new: true,
                                      },
                                      ...prev,
                                    ]);
                                  } else {
                                    setSelectedWorkspaces(prev =>
                                      prev.filter(s => s.id !== w.workspace_id)
                                    );
                                  }
                                }}
                              />
                            }
                            label={w.name}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  ))}
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    maxHeight: 250,
                    overflow: 'auto',
                  }}
                >
                  {selectedWorkspaces.map(ws => (
                    <Chip
                      shape="square"
                      key={ws.id}
                      label={ws.name || ws.id}
                      color={ws.new ? 'primary' : 'default'}
                      onDelete={() =>
                        setSelectedWorkspaces(prev =>
                          prev.filter(s => s.id !== ws.id)
                        )
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </Accordion>
          </>
        )}

        <Accordion title="Exempt Users" defaultExpanded={!!policyId}>
          <Stack sx={{gap: 2, overflow: 'hidden'}}>
            <Typography variant="body2" sx={{marginBlockEnd: 2}}>
              Users below are exempt from this policy (not blocked).
            </Typography>
            <UserAutocomplete
              placeholder="Search for a user by name or email"
              value={null}
              onChange={user => {
                // Add-only, same rationale as the org picker (PRODISSUE-1792):
                // removal is only via the chip delete below.
                if (user && !selectedUsers.some(s => s.id === user.id)) {
                  setSelectedUsers(prev => [{...user, new: true}, ...prev]);
                }
              }}
            />
            <Stack
              direction="row"
              sx={{
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                maxHeight: 250,
                overflow: 'auto',
              }}
            >
              {selectedUsers.map(user => (
                <Chip
                  shape="square"
                  label={user.full_name || user.email || user.id}
                  key={user.id}
                  color={user.new ? 'primary' : 'default'}
                  onDelete={() => {
                    setSelectedUsers(prev =>
                      prev.filter(prevOrg => prevOrg.id !== user.id)
                    );
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Accordion>
      </Stack>
      <Notifier
        open={!!updatePolicy.isPending}
        progress
        message="Updating policy..."
      />
      <Notifier
        open={!!createPolicy.isPending}
        progress
        message="Creating policy..."
      />
      <Notifier
        open={!!policyQuery.isError}
        severity="error"
        message="There was an error fetching policy data"
      />
    </Box>
  );
};

PolicyForm.propTypes = {
  policy: PropTypes.object,
};

export default PolicyForm;
